import type { ResourceFlow } from './processes';

export interface CpuChannel extends ResourceFlow {
  id: string;
  shareOfBusy: number;
  unattributed?: boolean;
}

export interface CpuWaterfall {
  busy: number;
  idle: number;
  channels: CpuChannel[];
  normalized: boolean;
}

export function cpuWaterfall(busyPercent: number | null, attributed: readonly ResourceFlow[]): CpuWaterfall | null {
  if (busyPercent === null || !Number.isFinite(busyPercent)) return null;
  const busy = Math.max(0, Math.min(100, busyPercent));
  const valid = attributed.filter(flow => Number.isFinite(flow.value) && flow.value > 0);
  const total = valid.reduce((sum, flow) => sum + flow.value, 0);
  const normalized = total > busy + 0.0001;
  const scale = total > busy && total > 0 ? busy / total : 1;
  const channels: CpuChannel[] = valid.map(flow => ({
    ...flow,
    id: flow.other ? 'other-processes' : `app:${flow.name.toLowerCase()}`,
    value: flow.value * scale,
    shareOfBusy: busy > 0 ? flow.value * scale / busy * 100 : 0,
  })).filter(flow => flow.value > 0);
  const unassigned = busy - channels.reduce((sum, flow) => sum + flow.value, 0);
  if (unassigned > 0.0001) channels.push({
    id: 'unattributed', name: 'Unattributed CPU', value: unassigned,
    shareOfBusy: busy > 0 ? unassigned / busy * 100 : 0, count: 0, unattributed: true,
  });
  return { busy, idle: 100 - busy, channels, normalized };
}

export type CpuScenario = 'Gaming' | 'Desktop' | 'Compiling';

const SCENARIOS: Record<CpuScenario, [string, number][]> = {
  Gaming: [['Game session', 0.62], ['Browser', 0.15], ['Steam', 0.1], ['System', 0.08], ['Other processes', 0.05]],
  Desktop: [['Browser', 0.42], ['Editor', 0.28], ['System', 0.15], ['Steam', 0.1], ['Other processes', 0.05]],
  Compiling: [['Build tools', 0.65], ['Editor', 0.18], ['Browser', 0.08], ['System', 0.05], ['Other processes', 0.04]],
};

export function demoCpuWaterfall(busy: number, scenario: CpuScenario): CpuWaterfall {
  return cpuWaterfall(busy, SCENARIOS[scenario].map(([name, share]) => ({
    name, value: Math.max(0, Math.min(100, busy)) * share,
    count: name === 'Other processes' ? 12 : 1, other: name === 'Other processes',
  })))!;
}